import BaseListPresenter from "../../../base/BaseListPresenter";

class EmployeeMasterPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.isMounted = false;
    this.filterDebounceTimeout = null;
    this.DEBOUNCE_DELAY = 300;
  }

  async componentDidMount() {
    this.isMounted = true;
    this.init();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.isMounted = false;
    // Clear any pending timeouts
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout);
    }
  }

  init() {
    const user = this.view.getCurrentUser();
    this.limit = 1000; // Set a high limit for employee list
    this.where = {}; // No initial filters
    this.search = {};
    this.filter = {};
    this.include = [];
    this.keys = undefined;
    this.sort = { lastName: 1 }; // Sort by last name by default
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    if (!this.isMounted) return;

    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();

      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = await this.findObjectUseCase.execute(collection, query);

      if (!this.isMounted) return;

      this.objects = this.objects.concat(objects);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
      this.hideProgress();
    } catch (error) {
      if (!this.isMounted) return;

      this.hideProgress();

      if (error.name !== "AbortError") {
        this.view.showError(error);
      }
    }
    this.progress = false;
  }

  async fetchEmployees() {
    try {
      const employees = await this.findObjectUseCase.execute(
        "employees",
        {},
        { lastName: 1 },
        0,
        1000
      );
      return employees;
    } catch (error) {
      console.error("Error fetching employees:", error);
      this.view.showMessage("Failed to load employee data", true);
      return [];
    }
  }

  // Helper method to filter employees by name
  filterByName(name) {
    if (!name) {
      this.filter = {};
    } else {
      this.filter = {
        $or: [
          { firstName: { $regex: name, $options: "i" } },
          { lastName: { $regex: name, $options: "i" } },
        ],
      };
    }

    this.reset();
    this.getObjects();
  }

  // Helper method to filter employees by position
  filterByPosition(position) {
    if (!position) {
      this.filter = {};
    } else {
      this.filter = {
        position: { $regex: position, $options: "i" },
      };
    }

    this.reset();
    this.getObjects();
  }

  // Helper method to filter employees by employment status
  filterByStatus(status) {
    if (!status) {
      this.filter = {};
    } else {
      this.filter = {
        employmentStatus: status,
      };
    }

    this.reset();
    this.getObjects();
  }

  debouncedFilter(filterFn) {
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout);
    }

    this.filterDebounceTimeout = setTimeout(() => {
      if (this.isMounted) {
        filterFn();
      }
    }, this.DEBOUNCE_DELAY);
  }

  reset() {
    super.reset();

    this.objects = [];
    this.current = 1;
    this.total = 0;

    if (this.view) {
      this.view.setObjects([]);
      this.view.setTotal(0);
    }
  }
}

export default EmployeeMasterPagePresenter;
